import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { Input, Button, Row, Col, Tooltip, Switch, Form } from "antd";
import { notificationSuccess, notificationError } from "utils/libHelpers/AntdNotification";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";

interface CloneOptionsConfig {
  name: string;
  relativeTo?: API_PROJECT_STARTWITH | API_PROJECT_FUNCTIONS;
  value: keyof API_CloneProjectOptions;
  additionalRequirements?: string;
}

interface CloneOptionsSelectionProps extends WithTranslation {
  parentData: { [key: string]: boolean };
  value?: API_CloneProjectOptions;
  onChange?: (v: API_CloneProjectOptions) => API_CloneProjectOptions;
}

const CloneOptionsSelection = withTranslation(I18N_NS.ADMIN)(
  class extends React.Component<CloneOptionsSelectionProps> {
    private config: {
      basic: CloneOptionsConfig[];
      files: CloneOptionsConfig[];
      other: CloneOptionsConfig[];
    } = {
      basic: [
        {
          name: "Disclaimer",
          value: "disclaimer",
        },
        {
          name: "Public Groups",
          value: "groups",
        },
        {
          name: "Logo",
          value: "logo",
        },
      ],
      files: [
        {
          name: "Directories",
          relativeTo: "DOCUMENTS",
          value: "directories",
        },
        {
          name: "Access rights for groups",
          relativeTo: "DOCUMENTS",
          value: "groupsAccessRights",
        },
        {
          name: "Filename rules",
          relativeTo: "FILENAMECHECK",
          value: "filenameRules",
        },
        {
          name: "Meta data",
          relativeTo: "METADATA",
          value: "metadata",
        },
        {
          name: "Automatic directory mapping",
          relativeTo: "AUTOMATICDIRMAPPING",
          value: "dirMapping",
        },
      ],
      other: [
        {
          name: "Q&A",
          relativeTo: "QA",
          value: "qa",
          additionalRequirements: "This module requires groups and organisations",
        },
        {
          name: "Views",
          relativeTo: "VIEW",
          value: "views",
          additionalRequirements: "This module requires metadata and file name rules",
        },
        {
          name: "Workflows",
          relativeTo: "WORKFLOW",
          value: "workflows",
        },
        {
          name: "Call for Tenders",
          relativeTo: "TENDER_MODERN",
          value: "cftSettings",
        },
      ],
    };

    handleChange = (option: keyof API_CloneProjectOptions) => {
      const { onChange, value = {} as API_CloneProjectOptions } = this.props;
      if (typeof onChange !== "function") {
        console.warn("onChange handler is not set up!");
        return;
      }
      let newValue = {} as API_CloneProjectOptions;
      if (Object.prototype.hasOwnProperty.call(value, option) && value[option] === false) {
        newValue = { ...value, [option]: true };
      } else {
        newValue = { ...value, [option]: false };
      }
      onChange(newValue);
    };

    renderSwitcher = (items: CloneOptionsConfig[]) =>
      items.map(({ name, value, relativeTo, additionalRequirements }) => {
        const { t } = this.props;
        return (
          <h4 key={value}>
            <Switch
              onChange={() => this.handleChange(value)}
              disabled={relativeTo && !this.props.parentData[relativeTo]}
              style={{ marginRight: `8px` }}
            />
            {t(name)}
            {additionalRequirements && (
              <Tooltip title={t(additionalRequirements)}>
                <FontAwesomeIcon
                  style={{ margin: `0 5px` }}
                  icon={["far", "info-circle"]}
                  size="xs"
                  color="#0000ff"
                  fixedWidth={true}
                />
              </Tooltip>
            )}
          </h4>
        );
      });

    render() {
      const { t } = this.props;
      return (
        <Row gutter={20} className="mb-lg">
          <Col xs={24} md={8}>
            <h2>{t("Basics")}</h2>
            {this.renderSwitcher(this.config.basic)}
          </Col>
          <Col xs={24} md={8}>
            <h2>{t("Module Documents")}</h2>
            {this.renderSwitcher(this.config.files)}
          </Col>
          <Col xs={24} md={8}>
            <h2>{t("Other modules")}</h2>
            {this.renderSwitcher(this.config.other)}
          </Col>
        </Row>
      );
    }
  }
);

export interface CloneProps extends WithTranslation, WithRouterProps {
  // this flag changes the endpoint which is used to get project info wether a Template project is cloned
  // or a regular one
  // https://pmgredesign.atlassian.net/browse/DEV-6798?focusedCommentId=40950
  cloneProjectTemplate?: boolean;
}

interface CloneState {
  loading: boolean;
  data: API_AdminProject;
}

export const Clone = withTranslation(I18N_NS.ADMIN)(
  withRouter(
    class extends React.Component<CloneProps, CloneState> {
      private initialData: API_CloneProjectOptions = {
        directories: false,
        dirMapping: false,
        disclaimer: false,
        filenameRules: false,
        groupsAccessRights: false,
        logo: false,
        metadata: false,
        groups: false,
        qa: false,
        views: false,
        workflows: false,
        cftSettings: false,
      };

      constructor(props: CloneProps) {
        super(props);
        this.state = {
          data: null,
          loading: false,
        };
      }

      componentDidMount() {
        (async () => await this.fetch())();
      }

      async fetch() {
        try {
          this.setState({ loading: true });
          const { params } = this.props;
          let url: string;
          if (this.props.cloneProjectTemplate) {
            url = API_URL.ADMIN_TEMPLATES.getTemplate(params.id);
          } else {
            url = API_URL.ADMIN_PROJECTS.getProject(params.id);
          }
          const response = await API.getData(url);
          this.setState({ data: response });
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
        }
      }

      get parentData() {
        const { data } = this.state;
        const { METADATA, FILENAMECHECK, AUTOMATICDIRMAPPING } = data.functions;
        const { DOCUMENTS, WORKFLOW, VIEW, ORGANIZATIONS, QA, TENDER_MODERN } = data.modules;

        return {
          DOCUMENTS,
          WORKFLOW,
          VIEW,
          QA,
          ORGANIZATIONS,
          METADATA,
          FILENAMECHECK,
          AUTOMATICDIRMAPPING,
          TENDER_MODERN,
        };
      }

      handleSubmit = async (values: API_CloneProject) => {
        const { t, params } = this.props;
        try {
          this.setState({ loading: true });
          const response: API_AdminProject = await API.newData(API_URL.ADMIN_PROJECTS.cloneProject(params.id), values);
          if (response.id && !values.template) {
            notificationSuccess(t("Success"), t("Project cloned"));
            this.props.navigate(`/admin/projects/edit/${response.id}`);
          } else {
            notificationSuccess(t("Success"), t("Project cloned as template"));
            this.props.navigate(`/admin/projects`);
          }
        } catch (error) {
          if (error.response && error.response.status === 409) {
            switch (error.response.data.code) {
              case 1066:
                notificationError(t("Project short name already exists"), t("Please provide another short name"));
                throw error;
              default:
                throw error;
            }
          } else {
            notificationError(t("Error"), t("Project was not cloned"));
            throw error;
          }
        } finally {
          this.setState({ loading: false });
        }
      };

      render() {
        const { t } = this.props;
        const { loading, data } = this.state;

        if (!data) {
          return null;
        }

        return (
          <div className="g-card">
            <Form onFinish={this.handleSubmit} layout="vertical">
              <Row justify="space-between" align="middle">
                <h1>{t("Clone project")}</h1>
                <div style={{ display: "flex", alignItems: "baseline" }}>
                  <Form.Item className="mb-2" name={"template"} initialValue={false} valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <span
                    style={{
                      marginLeft: 5,
                      fontSize: 18,
                      verticalAlign: "middle",
                    }}
                  >
                    {t("Create as template project")}
                  </span>
                </div>
              </Row>
              <Row justify="space-between" gutter={20} className="mb-lg">
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    label={t("Name")}
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: t("Please provide a project name"),
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    label={t("Short name")}
                    name="shortname"
                    rules={[
                      {
                        required: true,
                        message: t("Please provide a short name"),
                      },
                    ]}
                  >
                    <Input
                      suffix={
                        <Tooltip title={t("Is used as part of the mail sender address")}>
                          <FontAwesomeIcon icon={["far", "question-circle"]} />
                        </Tooltip>
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    label={t("Description")}
                    name="description"
                    rules={[
                      {
                        required: true,
                        message: t("Please provide a description"),
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="options" initialValue={this.initialData}>
                <CloneOptionsSelection parentData={this.parentData} />
              </Form.Item>
              <Row className="mb-2">
                <FontAwesomeIcon
                  style={{ margin: `0 7px` }}
                  icon={["far", "exclamation-triangle"]}
                  size="lg"
                  color="#ff0000"
                  fixedWidth={true}
                />
                <strong>{t("Always cloned")}:&nbsp;</strong>
                <span>{t("All Viewer settings, all File Check Mobile settings")}</span>
              </Row>
              <Row className="mb-lg">
                <FontAwesomeIcon
                  style={{ margin: `0 7px` }}
                  icon={["far", "exclamation-triangle"]}
                  size="lg"
                  color="#ff0000"
                  fixedWidth={true}
                />
                <strong>{t("Never cloned")}:&nbsp;</strong>
                <span>{t(`Users or contacts, "Print Jobs Tab" for project admins`)}</span>
              </Row>

              <Row justify="space-between" style={{ marginTop: "20px" }}>
                <Button onClick={() => this.props.navigate("/admin/projects")}>{t("Cancel")}</Button>
                <Button type="primary" loading={loading} htmlType="submit">
                  {t("Create")}
                </Button>
              </Row>
            </Form>
          </div>
        );
      }
    }
  )
);
